/**
 * Utility function for generating QR codes with MasterJi logo embedded in the center.
 * Uses Canvas API to overlay the logo on the QR code.
 */

import QRCode from 'qrcode';
import logoImage from '../assets/logo.png';

interface QRCodeOptions {
  width?: number;
  margin?: number;
  color?: {
    dark?: string;
    light?: string;
  };
  logoSizeRatio?: number; // Ratio of logo size to QR code size (0.0 to 0.3)
}

/**
 * Generate a QR code with MasterJi logo embedded in the center.
 * 
 * @param data - The data to encode in the QR code
 * @param options - QR code generation options
 * @returns Promise resolving to data URL string of the QR code image
 */
export async function generateQRCodeWithLogo(
  data: string,
  options: QRCodeOptions = {}
): Promise<string> {
  const {
    width = 256,
    margin = 2,
    color = {
      dark: '#000000',
      light: '#FFFFFF'
    },
    logoSizeRatio = 0.2 // Logo will be 20% of QR code size
  } = options;

  try {
    // Generate QR code as data URL
    const qrDataUrl = await QRCode.toDataURL(data, {
      width,
      margin,
      color
    });

    // Create a canvas to overlay the logo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Load QR code image
    const qrImage = new Image();
    await new Promise<void>((resolve, reject) => {
      qrImage.onload = () => resolve();
      qrImage.onerror = reject;
      qrImage.src = qrDataUrl;
    });

    // Set canvas size to match QR code
    canvas.width = qrImage.width;
    canvas.height = qrImage.height;

    // Draw QR code on canvas
    ctx.drawImage(qrImage, 0, 0);

    // Load and overlay logo
    try {
      const logo = new Image();
      logo.crossOrigin = 'anonymous'; // Handle CORS if needed
      
      await new Promise<void>((resolve, reject) => {
        logo.onload = () => resolve();
        logo.onerror = (error) => {
          console.warn('Could not load logo, generating QR code without logo:', error);
          resolve(); // Continue without logo
        };
        logo.src = logoImage;
      });

      if (logo.complete && logo.width > 0 && logo.height > 0) {
        // Calculate logo size (20% of QR code size)
        const logoSize = Math.min(canvas.width, canvas.height) * logoSizeRatio;
        
        // Calculate logo position (center)
        const logoX = (canvas.width - logoSize) / 2;
        const logoY = (canvas.height - logoSize) / 2;

        // Create a white background for logo (to ensure it's visible)
        const logoBgSize = logoSize + 10; // Add padding
        const logoBgX = (canvas.width - logoBgSize) / 2;
        const logoBgY = (canvas.height - logoBgSize) / 2;

        // Draw white background
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(logoBgX, logoBgY, logoBgSize, logoBgSize);

        // Draw logo maintaining aspect ratio
        const logoAspectRatio = logo.width / logo.height;
        let drawWidth = logoSize;
        let drawHeight = logoSize;
        
        if (logoAspectRatio > 1) {
          // Logo is wider
          drawHeight = logoSize / logoAspectRatio;
        } else {
          // Logo is taller
          drawWidth = logoSize * logoAspectRatio;
        }

        const logoDrawX = (canvas.width - drawWidth) / 2;
        const logoDrawY = (canvas.height - drawHeight) / 2;

        ctx.drawImage(logo, logoDrawX, logoDrawY, drawWidth, drawHeight);
      }
    } catch (logoError) {
      console.warn('Error overlaying logo on QR code:', logoError);
      // Continue without logo - QR code is still valid
    }

    // Convert canvas to data URL
    return canvas.toDataURL('image/png');
  } catch (_error) {
    console.error('Error generating QR code with logo:', error);
    // Fallback to QR code without logo
    return QRCode.toDataURL(data, {
      width,
      margin,
      color
    });
  }
}

/**
 * Generate QR code with logo (alternative method using image URL)
 * Useful when logo path needs to be specified dynamically
 */
export async function generateQRCodeWithLogoFromUrl(
  data: string,
  logoUrl: string,
  options: QRCodeOptions = {}
): Promise<string> {
  const {
    width = 256,
    margin = 2,
    color = {
      dark: '#000000',
      light: '#FFFFFF'
    },
    logoSizeRatio = 0.2
  } = options;

  try {
    // Generate QR code as data URL
    const qrDataUrl = await QRCode.toDataURL(data, {
      width,
      margin,
      color
    });

    // Create a canvas to overlay the logo
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    if (!ctx) {
      throw new Error('Could not get canvas context');
    }

    // Load QR code image
    const qrImage = new Image();
    await new Promise<void>((resolve, reject) => {
      qrImage.onload = () => resolve();
      qrImage.onerror = reject;
      qrImage.src = qrDataUrl;
    });

    // Set canvas size to match QR code
    canvas.width = qrImage.width;
    canvas.height = qrImage.height;

    // Draw QR code on canvas
    ctx.drawImage(qrImage, 0, 0);

    // Load and overlay logo
    const logo = new Image();
    logo.crossOrigin = 'anonymous';
    
    await new Promise<void>((resolve, reject) => {
      logo.onload = () => resolve();
      logo.onerror = (error) => {
        console.warn('Could not load logo, generating QR code without logo:', error);
        resolve(); // Continue without logo
      };
      logo.src = logoUrl;
    });

    if (logo.complete && logo.width > 0 && logo.height > 0) {
      // Calculate logo size
      const logoSize = Math.min(canvas.width, canvas.height) * logoSizeRatio;
      
      // Create white background for logo
      const logoBgSize = logoSize + 10;
      const logoBgX = (canvas.width - logoBgSize) / 2;
      const logoBgY = (canvas.height - logoBgSize) / 2;

      ctx.fillStyle = '#FFFFFF';
      ctx.fillRect(logoBgX, logoBgY, logoBgSize, logoBgSize);

      // Draw logo maintaining aspect ratio
      const logoAspectRatio = logo.width / logo.height;
      let drawWidth = logoSize;
      let drawHeight = logoSize;
      
      if (logoAspectRatio > 1) {
        drawHeight = logoSize / logoAspectRatio;
      } else {
        drawWidth = logoSize * logoAspectRatio;
      }

      const logoDrawX = (canvas.width - drawWidth) / 2;
      const logoDrawY = (canvas.height - drawHeight) / 2;

      ctx.drawImage(logo, logoDrawX, logoDrawY, drawWidth, drawHeight);
    }

    // Convert canvas to data URL
    return canvas.toDataURL('image/png');
  } catch (_error) {
    console.error('Error generating QR code with logo:', error);
    // Fallback to QR code without logo
    return QRCode.toDataURL(data, {
      width,
      margin,
      color
    });
  }
}

